import type { MarketEvent, Session } from '@/mocks/marketCalendar';

export function toUtcMinutes(time: string): number {
  const [h, m] = time.split(':').map(Number);
  return h * 60 + m;
}

export function minutesToLabel(mins: number): string {
  const m = ((mins % 1440) + 1440) % 1440;
  const h = Math.floor(m / 60);
  const mm = Math.floor(m % 60);
  return `${String(h).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
}

export function minutesToLabel12(mins: number): string {
  const m = ((mins % 1440) + 1440) % 1440;
  let h = Math.floor(m / 60);
  const mm = Math.floor(m % 60);
  const suffix = h >= 12 ? 'PM' : 'AM';
  h = h % 12;
  if (h === 0) h = 12;
  return `${h}:${String(mm).padStart(2, '0')} ${suffix}`;
}

export function sessionIsOpen(session: Session, nowUtcMinute: number): boolean {
  if (session.startUtc < session.endUtc) {
    return nowUtcMinute >= session.startUtc && nowUtcMinute < session.endUtc;
  }
  // wraps midnight (Sydney)
  return nowUtcMinute >= session.startUtc || nowUtcMinute < session.endUtc;
}

export function sessionStatus(session: Session, nowUtcMinute: number): 'open' | 'closed' | 'starts-in' {
  if (sessionIsOpen(session, nowUtcMinute)) return 'open';
  // time until next open
  const untilOpen = ((session.startUtc - nowUtcMinute) % 1440 + 1440) % 1440;
  // "starts in" if within next 2 hours
  return untilOpen <= 120 ? 'starts-in' : 'closed';
}

export function minutesUntil(minuteTarget: number, nowMinute: number): number {
  return ((minuteTarget - nowMinute) % 1440 + 1440) % 1440;
}

export function formatCountdown(ms: number): string {
  if (ms <= 0) return '00:00:00';
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

export function formatCountdownText(ms: number): string {
  if (ms <= 0) return 'Đã diễn ra';
  const totalSec = Math.floor(ms / 1000);
  const d = Math.floor(totalSec / 86400);
  const h = Math.floor((totalSec % 86400) / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (d > 0) return `${d}ng ${h}g ${m}p`;
  if (h > 0) return `${h}g ${m}p ${s}s`;
  return `${m}p ${s}s`;
}

// Absolute UTC timestamp (ms) of an event for its dayOffset
export function eventTimestamp(e: MarketEvent): number {
  const now = new Date();
  const day = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + e.dayOffset));
  day.setUTCHours(0, 0, 0, 0);
  return day.getTime() + toUtcMinutes(e.time) * 60000;
}

export function weekdayLabel(dayOffset: number): string {
  const now = new Date();
  const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + dayOffset));
  const days = ['CN', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7'];
  return days[d.getUTCDay()];
}

export function weekdayFull(dayOffset: number): string {
  const now = new Date();
  const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + dayOffset));
  const days = ['Chủ nhật', 'Thứ hai', 'Thứ ba', 'Thứ tư', 'Thứ năm', 'Thứ sáu', 'Thứ bảy'];
  return days[d.getUTCDay()];
}

export function dateLabel(dayOffset: number): string {
  const now = new Date();
  const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + dayOffset));
  const months = ['Tháng 1', 'Tháng 2', 'Tháng 3', 'Tháng 4', 'Tháng 5', 'Tháng 6', 'Tháng 7', 'Tháng 8', 'Tháng 9', 'Tháng 10', 'Tháng 11', 'Tháng 12'];
  return `${String(d.getUTCDate()).padStart(2, '0')} ${months[d.getUTCMonth()]}, ${d.getUTCFullYear()}`;
}

export function dayNumber(dayOffset: number): number {
  const now = new Date();
  const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + dayOffset));
  return d.getUTCDate();
}

export function monthShort(dayOffset: number): string {
  const now = new Date();
  const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + dayOffset));
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return months[d.getUTCMonth()];
}